import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MEDIA_VARIANTS } from '../../media';
import { ImageLoader } from '../Game/ImageLoader';
import {
  createLootContainerType,
  getItemTypes,
  getItems,
  getLootContainerTypes,
  getRarity,
  handleMediaUpload,
  updateLootContainerType,
} from './AdminAPI';
import SearchPickerDialog from './WorldEditor/SearchPickerDialog';

const read = (value, camel, pascal) => value?.[camel] ?? value?.[pascal];

function emptyType() {
  return {
    id: '',
    key: '',
    name: '',
    description: '',
    closedImageId: '',
    emptyImageId: '',
    renderScaleTiles: 1,
    capacity: 5,
    openDurationSeconds: 300,
    refillSeconds: 300,
    rarityDropChanceModifier: [],
    itemLevelOverride: '',
    minimumRandomRolls: 0,
    maximumRandomRolls: 0,
    guaranteedEntries: [],
    weightedEntries: [],
  };
}

function normalizeEntry(entry, weighted = false) {
  const base = {
    itemId: String(read(entry, 'itemId', 'ItemId') || ''),
    exclusiveToContainer:
      read(entry, 'exclusiveToContainer', 'ExclusiveToContainer') === true,
  };
  return weighted ? {
    ...base,
    weight: Number(read(entry, 'weight', 'Weight')) || 1,
    minimumQuantity: Math.max(
      1,
      Number(read(entry, 'minimumQuantity', 'MinimumQuantity')) || 1,
    ),
    maximumQuantity: Math.max(
      1,
      Number(read(entry, 'maximumQuantity', 'MaximumQuantity')) || 1,
    ),
  } : {
    ...base,
    quantity: Math.max(1, Number(read(entry, 'quantity', 'Quantity')) || 1),
  };
}

function normalizeType(value) {
  const guaranteed = read(value, 'guaranteedEntries', 'GuaranteedEntries');
  const weighted = read(value, 'weightedEntries', 'WeightedEntries');
  return {
    id: String(read(value, 'id', 'Id') || ''),
    key: String(read(value, 'key', 'Key') || ''),
    name: String(read(value, 'name', 'Name') || ''),
    description: String(read(value, 'description', 'Description') || ''),
    closedImageId: String(read(value, 'closedImageId', 'ClosedImageId') || ''),
    emptyImageId: String(read(value, 'emptyImageId', 'EmptyImageId') || ''),
    renderScaleTiles: Number(
      read(value, 'renderScaleTiles', 'RenderScaleTiles'),
    ) || 1,
    capacity: Number(read(value, 'capacity', 'Capacity')) || 5,
    openDurationSeconds: Number(read(
      value,
      'openDurationSeconds',
      'OpenDurationSeconds',
    )) || 300,
    refillSeconds: Number(read(value, 'refillSeconds', 'RefillSeconds')) || 300,
    rarityDropChanceModifier: (read(
      value,
      'rarityDropChanceModifier',
      'RarityDropChanceModifier',
    ) || []).map(modifier => ({
      rarityId: String(read(modifier, 'rarityId', 'RarityId') || ''),
      dropChanceModifier: Number(read(
        modifier,
        'dropChanceModifier',
        'DropChanceModifier',
      ) ?? 1),
    })),
    itemLevelOverride:
      read(value, 'itemLevelOverride', 'ItemLevelOverride') ?? '',
    minimumRandomRolls: Number(
      read(value, 'minimumRandomRolls', 'MinimumRandomRolls'),
    ) || 0,
    maximumRandomRolls: Number(
      read(value, 'maximumRandomRolls', 'MaximumRandomRolls'),
    ) || 0,
    guaranteedEntries: (Array.isArray(guaranteed) ? guaranteed : [])
      .map(entry => normalizeEntry(entry)),
    weightedEntries: (Array.isArray(weighted) ? weighted : [])
      .map(entry => normalizeEntry(entry, true)),
  };
}

function payload(draft) {
  return {
    ...draft,
    key: draft.key.trim().toLowerCase(),
    name: draft.name.trim(),
    description: draft.description.trim(),
    capacity: Number(draft.capacity),
    openDurationSeconds: Number(draft.openDurationSeconds),
    refillSeconds: Number(draft.refillSeconds),
    renderScaleTiles: Number(draft.renderScaleTiles),
    itemLevelOverride: draft.itemLevelOverride === ''
      ? null
      : Number(draft.itemLevelOverride),
    minimumRandomRolls: Number(draft.minimumRandomRolls),
    maximumRandomRolls: Number(draft.maximumRandomRolls),
  };
}

function ItemPicker({ item, items, onChange }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const options = useMemo(() => items.map(candidate => ({
    value: String(read(candidate, 'id', 'Id') || ''),
    label: String(read(candidate, 'name', 'Name') || 'Item'),
    group: String(read(candidate, 'typeName', 'TypeName') || 'Items'),
    meta: read(candidate, 'visible', 'Visible') === false ? 'Hidden item' : '',
  })), [items]);

  return (
    <>
      <button
        className="button-secondary"
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
      >
        {item?.name || 'Choose item…'}
      </button>
      {open ? (
        <SearchPickerDialog
          onApply={values => {
            onChange(values[0] || '');
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
          options={options}
          returnFocusRef={triggerRef}
          selectedValues={item ? [item.id] : []}
          title="Choose loot item"
        />
      ) : null}
    </>
  );
}

function EntryEditor({
  entries,
  items,
  kind,
  onChange,
}) {
  const weighted = kind === 'weighted';
  const byId = new Map(items.map(item => [item.id, item]));
  const add = () => onChange([...entries, weighted ? {
    itemId: '',
    weight: 1,
    minimumQuantity: 1,
    maximumQuantity: 1,
    exclusiveToContainer: false,
  } : {
    itemId: '',
    quantity: 1,
    exclusiveToContainer: false,
  }]);
  const patch = (index, value) => onChange(entries.map(
    (entry, entryIndex) => (
      entryIndex === index ? { ...entry, ...value } : entry
    ),
  ));

  return (
    <fieldset className="admin-progression-fieldset">
      <legend>{weighted ? 'Weighted random loot' : 'Guaranteed loot'}</legend>
      {entries.map((entry, index) => (
        <div className="admin-progression-row" key={`${kind}-${index}`}>
          <ItemPicker
            item={byId.get(entry.itemId)}
            items={items}
            onChange={itemId => patch(index, { itemId })}
          />
          {weighted ? (
            <>
              <label>
                Weight
                <input
                  min="0.01"
                  step="0.01"
                  type="number"
                  value={entry.weight}
                  onChange={event => patch(index, {
                    weight: Number(event.target.value),
                  })}
                />
              </label>
              <label>
                Min quantity
                <input
                  min="1"
                  max="20"
                  type="number"
                  value={entry.minimumQuantity}
                  onChange={event => patch(index, {
                    minimumQuantity: Number(event.target.value),
                  })}
                />
              </label>
              <label>
                Max quantity
                <input
                  min="1"
                  max="20"
                  type="number"
                  value={entry.maximumQuantity}
                  onChange={event => patch(index, {
                    maximumQuantity: Number(event.target.value),
                  })}
                />
              </label>
            </>
          ) : (
            <label>
              Quantity
              <input
                min="1"
                max="20"
                type="number"
                value={entry.quantity}
                onChange={event => patch(index, {
                  quantity: Number(event.target.value),
                })}
              />
            </label>
          )}
          <label>
            <input
              checked={entry.exclusiveToContainer}
              type="checkbox"
              onChange={event => patch(index, {
                exclusiveToContainer: event.target.checked,
              })}
            />
            Exclusive to this type
          </label>
          <button
            className="button-critical"
            type="button"
            onClick={() => onChange(entries.filter(
              (_entry, entryIndex) => entryIndex !== index,
            ))}
          >
            Remove
          </button>
        </div>
      ))}
      <button className="button-secondary" type="button" onClick={add}>
        Add {weighted ? 'weighted' : 'guaranteed'} item
      </button>
    </fieldset>
  );
}

export default function AdminLootContainerTypes() {
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [draft, setDraft] = useState(emptyType);
  const [creating, setCreating] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [issues, setIssues] = useState([]);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const [
        loadedTypes,
        loadedItems,
        loadedItemTypes,
        loadedRarities,
      ] = await Promise.all([
        getLootContainerTypes(),
        getItems(),
        getItemTypes(),
        getRarity(),
      ]);
      const itemTypeNames = new Map((loadedItemTypes || []).map(type => [
        String(read(type, 'id', 'Id') || ''),
        String(read(type, 'name', 'Name') || ''),
      ]));
      const normalizedItems = (loadedItems || []).map(item => ({
        ...item,
        id: String(read(item, 'id', 'Id') || ''),
        name: String(read(item, 'name', 'Name') || ''),
        typeName: itemTypeNames.get(String(
          read(item, 'typeId', 'TypeId') || '',
        )) || 'Items',
      }));
      const normalizedTypes = (loadedTypes || []).map(normalizeType);
      setTypes(normalizedTypes);
      setItems(normalizedItems);
      setRarities((loadedRarities || []).map(rarity => ({
        id: String(read(rarity, 'id', 'Id') || ''),
        name: String(read(rarity, 'name', 'Name') || ''),
        rank: Number(read(rarity, 'rank', 'Rank')) || 0,
      })).sort((left, right) => left.rank - right.rank));
      if (normalizedTypes.length > 0) {
        setDraft(normalizedTypes[0]);
        setCreating(false);
      }
    } catch (loadError) {
      setError(loadError?.message || 'Loot container types could not be loaded.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = types.filter(type => (
    `${type.name} ${type.key}`.toLowerCase().includes(search.toLowerCase())
  ));
  const update = (field, value) => setDraft(current => ({
    ...current,
    [field]: value,
  }));
  const upload = async (field, file) => {
    if (!file) return;
    setBusy(true);
    const id = await handleMediaUpload(file, 'progresslands.tile.v1');
    if (id) update(field, id);
    else setError('Container artwork could not be uploaded.');
    setBusy(false);
  };
  const save = async event => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setIssues([]);
    setMessage('');
    try {
      const saved = normalizeType(creating
        ? await createLootContainerType(payload(draft))
        : await updateLootContainerType(draft.id, payload(draft)));
      setTypes(current => [
        ...current.filter(type => type.id !== saved.id),
        saved,
      ].sort((left, right) => left.name.localeCompare(right.name)));
      setDraft(saved);
      setCreating(false);
      setMessage(`Loot container “${saved.name}” saved.`);
    } catch (saveError) {
      setError(saveError?.message || 'Loot container type could not be saved.');
      setIssues(saveError?.issues || []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-busy={busy} className="admin-settingspage admin-buildings">
      <aside aria-label="Loot container type list" className="admin_list">
        <button
          className="button-primary"
          disabled={busy}
          type="button"
          onClick={() => {
            setDraft(emptyType());
            setCreating(true);
            setError('');
            setIssues([]);
          }}
        >
          Create loot container
        </button>
        <label>
          Filter:
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </label>
        {filtered.map(type => (
          <button
            className={`listElement${draft.id === type.id ? ' is-active' : ''}`}
            key={type.id}
            type="button"
            onClick={() => {
              setDraft(type);
              setCreating(false);
              setError('');
              setIssues([]);
            }}
          >
            <span>{type.name}</span>
            <small>{type.key} · {type.capacity} slots</small>
          </button>
        ))}
      </aside>
      <section className="admin_details">
        <h1>Loot Containers</h1>
        <p>Create persistent world containers, their artwork, and bounded loot.</p>
        {error ? (
          <div className="admin-changelog__error" role="alert">
            <p>{error}</p>
            {issues.length > 0 ? (
              <ul>{issues.map(issue => <li key={issue}>{issue}</li>)}</ul>
            ) : null}
          </div>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
        <form onSubmit={save}>
          <label className="form-control">
            <span>Key</span>
            <input
              disabled={!creating}
              value={draft.key}
              onChange={event => update('key', event.target.value)}
            />
          </label>
          <label className="form-control">
            <span>Name</span>
            <input
              value={draft.name}
              onChange={event => update('name', event.target.value)}
            />
          </label>
          <label className="form-control">
            <span>Description</span>
            <textarea
              value={draft.description}
              onChange={event => update('description', event.target.value)}
            />
          </label>
          <div className="admin-progression-row">
            {[
              ['closedImageId', 'Closed artwork'],
              ['emptyImageId', 'Empty/open artwork'],
            ].map(([field, label]) => (
              <label key={field}>
                {label}
                <input
                  accept="image/png"
                  type="file"
                  onChange={event => upload(field, event.target.files?.[0])}
                />
                {draft[field] ? (
                  <ImageLoader
                    className="adminPreviewImage"
                    imageId={draft[field]}
                    variant={MEDIA_VARIANTS.TILE_DETAIL}
                    alt={`${label} preview`}
                  />
                ) : null}
              </label>
            ))}
          </div>
          <div className="admin-progression-row">
            <label>
              Render size (tiles)
              <input
                min="0.5"
                max="3"
                step="0.1"
                type="number"
                value={draft.renderScaleTiles}
                onChange={event => update(
                  'renderScaleTiles',
                  event.target.value,
                )}
              />
            </label>
            <label>
              Capacity
              <input
                min="1"
                max="25"
                type="number"
                value={draft.capacity}
                onChange={event => update('capacity', event.target.value)}
              />
            </label>
            <label>
              Open duration seconds
              <input
                min="1"
                type="number"
                value={draft.openDurationSeconds}
                onChange={event => update(
                  'openDurationSeconds',
                  event.target.value,
                )}
              />
            </label>
            <label>
              Refill seconds
              <input
                min="1"
                type="number"
                value={draft.refillSeconds}
                onChange={event => update('refillSeconds', event.target.value)}
              />
            </label>
            <label>
              Equipment item level override
              <input
                min="1"
                placeholder="Region level"
                type="number"
                value={draft.itemLevelOverride}
                onChange={event => update(
                  'itemLevelOverride',
                  event.target.value,
                )}
              />
            </label>
          </div>
          <fieldset className="admin-progression-fieldset">
            <legend>Rarity chance modifiers</legend>
            <p>These multiply the normal rarity weights when the chest is first opened.</p>
            {rarities.map(rarity => {
              const configured = draft.rarityDropChanceModifier.find(
                modifier => modifier.rarityId === rarity.id,
              );
              return (
                <label key={rarity.id}>
                  {rarity.name}
                  <input
                    min="0"
                    step="0.01"
                    type="number"
                    value={configured?.dropChanceModifier ?? 1}
                    onChange={event => update(
                      'rarityDropChanceModifier',
                      [
                        ...draft.rarityDropChanceModifier.filter(
                          modifier => modifier.rarityId !== rarity.id,
                        ),
                        {
                          rarityId: rarity.id,
                          dropChanceModifier: Number(event.target.value),
                        },
                      ],
                    )}
                  />
                </label>
              );
            })}
          </fieldset>
          <EntryEditor
            entries={draft.guaranteedEntries}
            items={items}
            kind="guaranteed"
            onChange={value => update('guaranteedEntries', value)}
          />
          <EntryEditor
            entries={draft.weightedEntries}
            items={items}
            kind="weighted"
            onChange={value => update('weightedEntries', value)}
          />
          <div className="admin-progression-row">
            <label>
              Minimum random draws
              <input
                min="0"
                type="number"
                value={draft.minimumRandomRolls}
                onChange={event => update(
                  'minimumRandomRolls',
                  event.target.value,
                )}
              />
            </label>
            <label>
              Maximum random draws
              <input
                min="0"
                type="number"
                value={draft.maximumRandomRolls}
                onChange={event => update(
                  'maximumRandomRolls',
                  event.target.value,
                )}
              />
            </label>
          </div>
          <button className="button-primary" disabled={busy} type="submit">
            {busy ? 'Saving…' : 'Save loot container'}
          </button>
        </form>
      </section>
    </main>
  );
}
